/* eslint-disable security/detect-object-injection */
import React, { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { IPostCartItems } from '../../../actions/post-cart.action';
import { IAlternativeUnits, IGetAllProductsResult } from '../../../actions/get-all-products.action';
import { CREATE_ORDER_COLUMN_INCOTERM } from '../../../configuration';
import { CustomCellContext, ProductTableCellProps } from '../lxs-create-order.data';
import { getConversionFactor } from '../../../utilities';

const ProductQuantityTableCellRef: React.FC<ProductTableCellProps> = ({
    props,
    headerColumnFilters,
    productsData,
    calculatePackageValue,
    updateProductQuantity,
    inputRefs,
    handleQuantityChange,
    handleQuantityMinimum,
    handleQuantityRoundup,
    resources
}) => {
    const parseNumber = (value: string | undefined) => (value ? Number(value.replace(/,/g, '.')) : undefined);
    const getConversionFactorForUnit = (unit: string | undefined) => {
        const minQtyUnit = relevantProduct?.AlternativeUnits.find(uom => uom.Unit === unit);
        return minQtyUnit ? Number(minQtyUnit.Numerator) / Number(minQtyUnit.Denomerator) : 1;
    };
    const getQuantityDisplayValue = (quantity: number) => {
        if (quantity <= 0) {
            return '';
        }
        return Number.isInteger(quantity) ? quantity.toFixed(0) : quantity.toFixed(3);
    };

    /**
     *  Remove trailing zeros
     * @param value
     * @returns
     */
    const formatDisplayValue = (value: string): string => {
        const numberValue = parseFloat(value);
        if (numberValue === 0) {
            return '';
        }
        return numberValue % 1 === 0 ? numberValue.toFixed(0) : numberValue.toFixed(3);
    };

    const columnFilterIncoterm = headerColumnFilters.find((filter: { id: string }) => filter.id === CREATE_ORDER_COLUMN_INCOTERM)
        ?.value as string;

    const customProps = props as CustomCellContext<IPostCartItems, string>;
    const currentProductIndex = customProps.row.index;
    const currentProduct = customProps.products[Number(currentProductIndex)];

    const { ProductNumber, UnitOfMeasure, EndCustomerNumber } = customProps.row.original;

    const [baseUOM, setBaseUOM] = useState<String>();
    const [value, setValue] = useState<string>(currentProduct.Quantity);
    const [lastUsedAUOM, setLastUsedAUOM] = useState<IAlternativeUnits>();
    const [previousProductsLength, setPreviousProductsLength] = useState<number>(customProps.products.length);
    const [displayValue, setDisplayValue] = useState<string>(getQuantityDisplayValue(Number(currentProduct.Quantity)));

    const relevantProduct = useMemo(() => {
        return productsData?.find(
            (product: { ProductNumber: string; Incoterm1: string }) =>
                product.ProductNumber === ProductNumber && product.Incoterm1 === columnFilterIncoterm
        );
    }, [productsData, ProductNumber, columnFilterIncoterm]);

    const endcustomerProduct = useMemo(() => {
        return relevantProduct?.EndCustomers.find(cust => cust.EndCustomerNumber === EndCustomerNumber);
    }, [relevantProduct, EndCustomerNumber]);

    /**
     * format minimum quantities and convert into matching unit
     */
    const relevantMinimumQty = useMemo(() => {
        const endCustomerMinQty = parseNumber(endcustomerProduct?.MinimumQty);
        const relevantProductMinQty = parseNumber(relevantProduct?.MinimumQty);

        if (endCustomerMinQty !== undefined && relevantProductMinQty !== undefined) {
            const useEndCustomerQty = endCustomerMinQty > relevantProductMinQty && endcustomerProduct?.MinimumQtyOnOrderLevel === false;
            const minQty = useEndCustomerQty ? endCustomerMinQty : relevantProductMinQty;
            const conversionFactor = getConversionFactorForUnit(
                useEndCustomerQty ? endcustomerProduct?.MinimumQtyUnit : relevantProduct?.MinimumQtyUnit
            );

            return (minQty * conversionFactor).toString();
        }

        if (endCustomerMinQty !== undefined && endcustomerProduct?.MinimumQtyOnOrderLevel === false) {
            return (endCustomerMinQty * getConversionFactorForUnit(endcustomerProduct?.MinimumQtyUnit)).toString();
        }

        if (relevantProductMinQty !== undefined) {
            return (relevantProductMinQty * getConversionFactorForUnit(relevantProduct?.MinimumQtyUnit)).toString();
        }

        return '0';
    }, [endcustomerProduct, relevantProduct]);

    const minimumQtyOnOrderLevel = endcustomerProduct?.MinimumQtyOnOrderLevel ? endcustomerProduct.MinimumQtyOnOrderLevel : false;
    const minimumQty = relevantMinimumQty ? relevantMinimumQty.replace(/,/g, '.') : '0';
    const packagingQty = relevantProduct?.PackagingQty ? relevantProduct.PackagingQty.replace(/,/g, '.') : '0';

    if (ProductNumber === relevantProduct?.ProductNumber && relevantProduct.Incoterm1 === columnFilterIncoterm) {
        if (UnitOfMeasure !== relevantProduct.UnitOfMeasure) {
            relevantProduct.UnitOfMeasure = currentProduct.UnitOfMeasure;
            setBaseUOM(relevantProduct.BaseUnit);
        }
    }

    /**
     * Store last amount of products to check for removed lines
     */
    useEffect(() => {
        setPreviousProductsLength(customProps.products.length);
    }, [customProps.products.length]);

    /**
     * Initial set of quantity based on packaging or minimum quantities
     */
    useEffect(() => {
        const isValueValid =
            (minimumQty === '' && packagingQty === '') || (Number(value) > Number(packagingQty) && Number(value) > Number(minimumQty));

        if (customProps.products.length < previousProductsLength || isValueValid) {
            return;
        }

        if (packagingQty || minimumQty) {
            // Reevaluate base unit for copied line items
            const baseUnit = relevantProduct?.AlternativeUnits.find(uom => {
                return uom.Unit === currentProduct.UnitOfMeasure;
            });
            let qtyLimit = Math.max(Number(packagingQty), Number(minimumQty));
            if (packagingQty || minimumQty) {
                if (
                    (baseUOM !== currentProduct.UnitOfMeasure && baseUOM !== undefined) ||
                    (baseUnit && relevantProduct?.BaseUnit !== baseUnit.Unit)
                ) {
                    const newUOM = relevantProduct?.AlternativeUnits.find(
                        (alternateUnit: { Unit: string }) => alternateUnit.Unit === currentProduct.UnitOfMeasure
                    );
                    if (newUOM && relevantProduct) {
                        const conversionFactor = getConversionFactor(relevantProduct, newUOM.Unit, true);
                        const calcQty = Number(qtyLimit) * conversionFactor;
                        const roundedPkgQty = calcQty;
                        qtyLimit = calculatePackageValue(Number(currentProduct.Quantity), Number(roundedPkgQty));
                        currentProduct.Quantity = qtyLimit.toString(); // eslint-disable-line security/detect-object-injection
                    }
                } else {
                    qtyLimit = calculatePackageValue(qtyLimit, Number(packagingQty));
                }
            }

            if (!minimumQtyOnOrderLevel) {
                let newQtyString = qtyLimit.toString();
                if (qtyLimit === 0) {
                    newQtyString = '';
                }
                setValue(newQtyString);
                setDisplayValue(formatDisplayValue(Number(newQtyString).toFixed(3)));
                updateProductQuantity(newQtyString, customProps, currentProductIndex);
            }
        }
    }, [minimumQty, minimumQtyOnOrderLevel]);

    useEffect(() => {
        if (relevantProduct?.UnitOfMeasure !== relevantProduct?.BaseUnit) {
            setBaseUOM(relevantProduct?.BaseUnit);
            const newUOM = relevantProduct?.AlternativeUnits.find(
                (alternateUnit: { Unit: string }) => alternateUnit.Unit === currentProduct.UnitOfMeasure
            );
            if (newUOM) {
                setLastUsedAUOM(newUOM);
            }
        }

        if (baseUOM !== currentProduct.UnitOfMeasure && baseUOM !== undefined) {
            const newUOM = relevantProduct?.AlternativeUnits.find(
                (alternateUnit: { Unit: string }) => alternateUnit.Unit === currentProduct.UnitOfMeasure
            );
            if (newUOM) {
                setLastUsedAUOM(newUOM);
            }
        }
    }, [ProductNumber]);

    /**
     * On product change, check for packaging units
     */
    useEffect(() => {
        let pkgQtyLimit = Number(packagingQty);
        if (!lastUsedAUOM) {
            if (relevantProduct) {
                const conversionFactor = getConversionFactor(relevantProduct, relevantProduct.UnitOfMeasure, true);
                const convertedPkgQty = Number(packagingQty) * conversionFactor;
                pkgQtyLimit = calculatePackageValue(Number(currentProduct.Quantity), Number(convertedPkgQty));
            }
            if (minimumQtyOnOrderLevel && Number(pkgQtyLimit) > 0 && Number(currentProduct.Quantity) < Number(pkgQtyLimit)) {
                setValue(packagingQty);
                setDisplayValue(Number(packagingQty) % 1 === 0 ? Number(packagingQty).toFixed(0) : Number(packagingQty).toFixed(3));
                updateProductQuantity(packagingQty, customProps, currentProductIndex);
            }
        }
    }, [customProps.products]);

    /**
     *  After blur, check for minimumQty and packagingQty. Update quantity values
     * @param currentPackagingQty
     * @param unitOfMeasure
     */
    const handleBlur = (currentPackagingQty: string | undefined, unitOfMeasure: string) => {
        if (minimumQty && packagingQty) {
            let minimumQtyValue = minimumQty;
            let packagingQtyValue = packagingQty;

            // Convert minimumQty and packagingQty in selected unit
            if (baseUOM !== unitOfMeasure && baseUOM !== undefined) {
                let calcQty = 0;
                let calcMinQty = 0;
                if (lastUsedAUOM) {
                    if (currentPackagingQty !== undefined) {
                        calcQty =
                            Number(currentPackagingQty.replace(/,/g, '.')) *
                            (Number(lastUsedAUOM.Denomerator) / Number(lastUsedAUOM.Numerator));
                    }
                    calcMinQty = Number(minimumQty) * (Number(lastUsedAUOM.Denomerator) / Number(lastUsedAUOM.Numerator));
                    minimumQtyValue = calcMinQty.toString();
                    packagingQtyValue = calcQty.toString();
                }
            }

            handleQuantityMinimum(value, minimumQtyValue, setValue, setDisplayValue, customProps, currentProductIndex, packagingQtyValue);
        } else if (!minimumQty && packagingQty) {
            if (baseUOM !== unitOfMeasure && baseUOM !== undefined) {
                let calcQty = 0;
                if (lastUsedAUOM) {
                    calcQty = Number(currentPackagingQty) * (Number(lastUsedAUOM.Denomerator) / Number(lastUsedAUOM.Numerator));
                }
                const newValue = calculatePackageValue(Number(value), calcQty);
                currentProduct.Quantity = newValue.toString(); // eslint-disable-line security/detect-object-injection
                handleQuantityRoundup(newValue, calcQty.toString(), customProps, currentProductIndex, setValue);
            } else {
                handleQuantityRoundup(value, packagingQty, customProps, currentProductIndex, setValue);
            }
        } else {
            updateProductQuantity(value, customProps, currentProductIndex);
            setValue(value);
            setDisplayValue(formatDisplayValue(value));
        }
    };

    /**
     *  Reset quantity to base unit
     * @param lastUsedAUOM
     * @param newUOM
     * @param currentProduct
     * @param customProps
     * @param currentProductIndex
     * @param packagingQtyNumber
     * @param setValue
     * @returns
     */
    const resetQuantity = (
        lastUsedAUOM: IAlternativeUnits | undefined,
        newUOM: IAlternativeUnits | undefined,
        currentProduct: IPostCartItems,
        customProps: CustomCellContext<IPostCartItems, string>,
        currentProductIndex: number,
        packagingQtyNumber: string | undefined,
        setValue: (qtyInput: string) => void
    ) => {
        if (!lastUsedAUOM || lastUsedAUOM.Unit === newUOM?.Unit) {
            return;
        }

        const calcQty = Number(currentProduct.Quantity) * (Number(lastUsedAUOM.Numerator) / Number(lastUsedAUOM.Denomerator));
        updateProductQuantity(calcQty.toString(), customProps, currentProductIndex);
        handleQuantityRoundup(calcQty, packagingQtyNumber, customProps, currentProductIndex, setValue);
    };

    /**
     *  Convert and format quantity
     * @param numerator
     * @param denomerator
     * @param currentProduct
     * @param customProps
     * @param currentProductIndex
     * @param setValue
     * @param setDisplayValue
     */
    const calculateAndSetQuantity = (
        numerator: number,
        denomerator: number,
        currentProduct: IPostCartItems,
        customProps: CustomCellContext<IPostCartItems, string>,
        currentProductIndex: number,
        setValue: (qtyInput: string) => void,
        setDisplayValue: (qtyInput: string) => void
    ) => {
        const calcQty = Number(currentProduct.Quantity) * (denomerator / numerator);
        updateProductQuantity(calcQty.toString(), customProps, currentProductIndex);
        setValue(calcQty.toString());
        setDisplayValue(formatDisplayValue(calcQty.toFixed(3)));
        currentProduct.Quantity = calcQty.toString();
    };

    /**
     *  Check for unit changes and handle conversion/formatting
     * @param event
     * @param customProps
     * @param currentProductIndex
     * @param relevantProduct
     * @param lastUsedAUOM
     * @param setLastUsedAUOM
     * @param setValue
     * @param setDisplayValue
     */
    const handleUnitChange = (
        event: ChangeEvent<HTMLSelectElement>,
        customProps: CustomCellContext<IPostCartItems, string>,
        currentProductIndex: number,
        relevantProduct: IGetAllProductsResult | undefined,
        lastUsedAUOM: IAlternativeUnits | undefined,
        setLastUsedAUOM: (uom: IAlternativeUnits) => void,
        setValue: (qtyInput: string) => void,
        setDisplayValue: (qtyInput: string) => void
    ) => {
        const newProducts = [...customProps.products];
        const currentNewProduct = newProducts[currentProductIndex];
        currentNewProduct.UnitOfMeasure = event.target.value;
        const newUOM = relevantProduct?.AlternativeUnits?.find(alternateUnit => alternateUnit.Unit === event.target.value);
        const packagingQtyNumber = packagingQty?.replace(/,/g, '.');
        customProps.setProducts(newProducts);

        if (newUOM) {
            resetQuantity(lastUsedAUOM, newUOM, currentNewProduct, customProps, currentProductIndex, packagingQtyNumber, setValue);

            calculateAndSetQuantity(
                Number(newUOM.Numerator),
                Number(newUOM.Denomerator),
                currentNewProduct,
                customProps,
                currentProductIndex,
                setValue,
                setDisplayValue
            );
            setLastUsedAUOM(newUOM);
        } else if (lastUsedAUOM) {
            calculateAndSetQuantity(
                Number(lastUsedAUOM.Denomerator),
                Number(lastUsedAUOM.Numerator),
                currentNewProduct,
                customProps,
                currentProductIndex,
                setValue,
                setDisplayValue
            );
        }
    };

    return (
        <div>
            <fieldset className='msc-form__input-group mb-0'>
                <div className='msc-form__input-group-container'>
                    {/* Quantity input */}
                    <input
                        id={`quantity-${currentProductIndex}`}
                        required
                        type='text'
                        value={displayValue || value}
                        ref={(el: HTMLInputElement) => (inputRefs.current[customProps.row.index] = el)}
                        onKeyDown={e => e.key === 'Enter' && e.preventDefault()}
                        onInput={event => (event.target as HTMLInputElement).setCustomValidity('')}
                        onChange={event => handleQuantityChange(event, setValue, setDisplayValue)}
                        onBlur={event => {
                            const { UnitOfMeasure } = currentProduct;
                            const target = event.target as HTMLInputElement;
                            handleBlur(packagingQty, UnitOfMeasure);
                            if (Number(value) === 0 && Number(currentProduct.Quantity) === 0) {
                                target.setCustomValidity(resources.createOrderFormErrorNonZero);
                            }
                        }}
                    />
                    {/* Unit of Measurement Select */}
                    <div className='msc-form__select-group-append'>
                        <select
                            className='msc-form__select-group-append-select'
                            id={`unitOfMeasure-${currentProductIndex}`}
                            value={currentProduct.UnitOfMeasure ?? undefined}
                            onChange={event =>
                                handleUnitChange(
                                    event,
                                    customProps,
                                    currentProductIndex,
                                    relevantProduct,
                                    lastUsedAUOM,
                                    setLastUsedAUOM,
                                    setValue,
                                    setDisplayValue
                                )
                            }
                        >
                            <option value={currentProduct.UnitOfMeasure}>{currentProduct.UnitOfMeasure}</option>
                            {relevantProduct?.AlternativeUnits?.filter(
                                alternateUnit => alternateUnit.Unit !== currentProduct.UnitOfMeasure
                            ).map(alternateUnit => (
                                <option value={alternateUnit.Unit} key={alternateUnit.Unit}>
                                    {alternateUnit.Unit}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            </fieldset>
        </div>
    );
};

export default ProductQuantityTableCellRef;
